/**
 * UN CYCLE DE MONTAGE D'UN MOTEUR VANILLA, sans React : ce que
 * `useMoteurVanilla` fait a chaque effet, sorti ici pour se prouver sans
 * navigateur (`tests/moteur-montage.test.mjs`).
 *
 * LE PIEGE QU'IL TIENT, CONSTATE EN BRANCHANT mind-elixir (app `carte-mentale`,
 * 29/09/2026). Sous StrictMode, React monte, demonte puis remonte l'effet. Le
 * premier montage est ANNULE avant d'avoir rendu son moteur (le `await` le
 * repousse d'une micro-tache), et le moteur annule est donc detruit APRES que
 * le second s'est installe. Or presque tous les moteurs vident leur element a
 * la destruction (`el.innerHTML = ''` chez mind-elixir) : si les deux
 * partagent l'element, la destruction du premier efface le second, et l'ecran
 * reste blanc en developpement, sans une erreur.
 *
 * D'OU UN HOTE PAR MONTAGE : un enfant neuf de l'element, cree ici, donne au
 * moteur, et retire au demontage. Un moteur ne vide jamais que SON hote ; un
 * moteur annule qui arrive en retard s'installe dans un hote deja retire de la
 * page, et y est detruit aussitot.
 */

/** Ce qu'un hote doit savoir faire : sortir de la page. */
export interface HoteDeMontage {
  remove(): void;
}

export interface CycleDeMontage<M, H extends HoteDeMontage> {
  /** Cree l'hote de CE montage (un enfant neuf de l'element) et l'attache. */
  creerHote(): H;
  /** Installe le moteur dans l'hote ; peut etre asynchrone. */
  monter(hote: H): M | Promise<M>;
  /** Le retire ; appele une fois par moteur installe. */
  demonter(moteur: M): void;
  /** Le moteur est pret (jamais appele si le cycle a ete annule entre-temps). */
  siMonte(moteur: M): void;
  /** Le montage a leve (jamais appele si le cycle a ete annule entre-temps). */
  siErreur(cause: unknown): void;
}

/** Lance un montage, rend son demontage (a appeler une fois). */
export function lancerMontage<M, H extends HoteDeMontage>(cycle: CycleDeMontage<M, H>): () => void {
  let annule = false;
  let monte: M | null = null;
  const hote = cycle.creerHote();
  const detruire = (moteur: M): void => {
    try {
      cycle.demonter(moteur);
    } catch (cause) {
      console.error('[moteur] Le moteur n a pas pu etre demonte :', cause);
    }
  };
  void (async () => {
    try {
      const instance = await cycle.monter(hote);
      if (annule) {
        detruire(instance);
        return;
      }
      monte = instance;
      cycle.siMonte(instance);
    } catch (cause) {
      if (!annule) cycle.siErreur(cause);
    }
  })();
  return () => {
    annule = true;
    if (monte !== null) detruire(monte);
    monte = null;
    hote.remove();
  };
}
